import React, { useState, useEffect, useRef, useCallback } from "react";
import Draggable from "react-draggable";
import "../../App.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAnglesDown,
  faAnglesRight,
  faClose,
  faCompress,
  faExpand,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { LocationMarkerPopup } from "../../mainLayer/MainWindow";
import clock from "../../assets/clock.png";
import { ResizableBox, ResizeCallbackData } from "react-resizable";
import AirQualityChart from "./AirQualityChart";
import { airQualityMap, openWindowsAirQualityMap } from "./airQuality";
import { map } from "../MainLayer";
import { Coordinate } from "ol/coordinate";
import Overlay from "ol/Overlay";

interface Props {
  sensorId: string;
  name: string;
  pm10: number;
  pm25: number;
  pm100: number;
  co2: number;
  temperature: number;
  aqi: number;
  timestamp: string;
  onClose: (incidentId: string) => void;
  addItem?: (id: string, type: "ew" | "river" | "air" | "hz" | "rain") => void;
  id?: string;
}

const AirQualityPopup: React.FC<Props> = (props) => {
  const [showDialog, setShowDialog] = useState<boolean>(true);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  const [showSensorDetails, setShowSensorDetails] = useState(false);
  const [activeFullScreen, setActiveFullScreen] = useState<boolean>(false);
  const fullscreenModal = useRef<HTMLDivElement | null>(null);
  const parentContainerRef = useRef<HTMLDivElement | null>(null);
  const [showDescription, setShowDescription] = useState(false);
  const [widthMain, setWidthMain] = useState<number>(0);
  const [heightMain, setHeightMain] = useState<number>(0);
  const [isDark, setIsDark] = useState<boolean>(false);

  const [dimensions, setDimensions] = useState({
    resizableBoxwidth: 0,
    resizableBoxheight: 0,
  });

  const [dimensionsMin, setDimensionsMin] = useState({
    resizableBoxMinwidth: 0,
    resizableBoxMinheight: 0,
  });

  //----------------resibleBox Function----------------

  const updateDimensions = () => {
    const vw = Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0
    );
    const vh = Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0
    );

    // setDimensions({
    //   resizableBoxwidth: vw * 0.68,
    //   resizableBoxheight: vh * 0.75,
    // });

    // setDimensionsMin({
    //   resizableBoxMinwidth: vw * 0.4,
    //   resizableBoxMinheight: vh * 0.72,
    // });

    if (vw > 2000 && vh > 1200) {
      setDimensions({
        resizableBoxwidth: vw * 0.4,
        resizableBoxheight: vh * 0.5,
      });
    } else {
      setDimensions({
        resizableBoxwidth: vw * 0.3,
        resizableBoxheight: vh * 0.6,
      });
    }

    if (vw > 2000 && vh > 1200) {
      setDimensionsMin({
        resizableBoxMinwidth: vw * 0.2,
        resizableBoxMinheight: vh * 0.3,
      });
    } else {
      setDimensionsMin({
        resizableBoxMinwidth: vw * 0.3,
        resizableBoxMinheight: vh * 0.4,
      });
    }
  };

  useEffect(() => {
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const openContentFullscreen = () => {
    if (!document.fullscreenElement) {
      enterFullscreen();
      setShowSensorDetails(true);
    } else {
      exitFullScreen();
      setShowSensorDetails(false);
    }
  };

  const enterFullscreen = () => {
    const elem = fullscreenModal.current;
    if (elem) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      }
      setActiveFullScreen(true);
    }
  };

  const exitFullScreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if ((document as any).mozCancelFullScreen) {
      (document as any).mozCancelFullScreen();
    } else if ((document as any).webkitExitFullscreen) {
      (document as any).webkitExitFullscreen();
    } else if ((document as any).msExitFullscreen) {
      (document as any).msExitFullscreen();
    }
    setActiveFullScreen(false);
  };

  useEffect(() => {
    const fetchCurrentSensorsData = async () => {
      const AIR_QUALITY_THREE_DAYS_URL =
      process.env.REACT_APP_AIR_QUALITY_THREE_DAYS_URL
      try {
        const response = await fetch(
         `${AIR_QUALITY_THREE_DAYS_URL}` +
            props.sensorId
        );
        const responseData = await response.json();

        if (responseData && typeof responseData === "object") {
          const { maxTimestamp } = responseData;
          setLastUpdate(maxTimestamp);
        } else {
          console.error("Invalid response data format:", responseData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchCurrentSensorsData();
    return () => {};
  }, [props.sensorId]);

  const date = new Date().toLocaleDateString();
  const time = new Date().toLocaleTimeString();

  const hideDialog = (incidentId: string) => {
    setShowDialog(false);
    LocationMarkerPopup("baseMap");
    if (airQualityMap.has(incidentId)) {
      airQualityMap.delete(incidentId);
    }
    props.onClose(incidentId);
    removeOverlay(incidentId);
    if (airQualityMap.has(incidentId)) {
      airQualityMap.delete(incidentId);
      openWindowsAirQualityMap.set(incidentId, "close");
    }
    const entries = Array.from(airQualityMap.values());
    const lastEntryIndex = entries.length - 1;
    entries.forEach((details, index) => {
      const { coordinates, sensorId } = details;
      if (coordinates) {
        removeOverlay(sensorId!);
        if (index === lastEntryIndex) {
          addOverlayClickItemLast(sensorId!, coordinates);
        } else {
          addOverlayClickItem(sensorId!, coordinates);
        }
      }
    });
  };

  const removeOverlay = (key: string) => {
    const overlays = map.getOverlays().getArray();
    const overlaysToRemove = overlays.filter(
      (overlay) => overlay.getId() === key
    );
    overlaysToRemove.forEach((overlay) => {
      map.removeOverlay(overlay);
    });
  };

  const addOverlayClickItem = (id: string, coordinates: Coordinate) => {
    const markerElement = document.createElement("div");
    markerElement.className = "location-dot animate";

    const ringElement = document.createElement("div");
    ringElement.className = "location-dot-ring";

    markerElement.appendChild(ringElement);
    const overlay = new Overlay({
      id: id,
      position: coordinates,
      positioning: "center-center",
      element: markerElement,
      stopEvent: false,
    });
    map.addOverlay(overlay);
  };

  const addOverlayClickItemLast = (id: string, coordinates: Coordinate) => {
    const markerElement = document.createElement("div");
    markerElement.className = "locationlast-dot animate";
    const ringElement = document.createElement("div");
    ringElement.className = "locationlast-dot-ring";

    markerElement.appendChild(ringElement);
    const overlay = new Overlay({
      id: id,
      position: coordinates,
      positioning: "center-center",
      element: markerElement,
      stopEvent: false,
    });
    map.addOverlay(overlay);
  };

  const setAqiCondition = (aqiValue: number): string => {
    if (aqiValue !== null) {
      if (0 < aqiValue && aqiValue <= 50) {
        return "Good";
      } else if (50 < aqiValue && aqiValue <= 100) {
        return "Moderate";
      } else if (100 < aqiValue && aqiValue <= 150) {
        return "Unhealthy for sensitive group";
      } else if (150 < aqiValue && aqiValue <= 200) {
        return "Unhealthy";
      } else if (200 < aqiValue && aqiValue <= 300) {
        return "Very unhealthy";
      } else if (300 < aqiValue && aqiValue <= 500) {
        return "Hazardous";
      }
    }
    return "Good";
  };

  useEffect(() => {}, [showDescription]);

  useEffect(() => {}, [widthMain, heightMain]);

  const handleResize = (
    event: React.SyntheticEvent,
    data: ResizeCallbackData
  ) => {
    setWidthMain(data.size.width);
    setHeightMain(data.size.height);

    if (data.size.width < 900) {
      // Set your desired width threshold
      setShowDescription(true);
    } else {
      setShowDescription(false);
    }
  };

  const popupRef = useRef<HTMLDivElement | null>(null);
  const enteredRef = useRef<boolean>(false);

  const mouseMoveEvent = useCallback((event: MouseEvent | TouchEvent) => {
    const sideMenu = document.getElementById("side-menu-wrapper");
  
    if (sideMenu && !enteredRef.current) {
      const sideMenuWidth = sideMenu.getBoundingClientRect();
      const sideMenuLeft = sideMenuWidth.left;
  
      const clientX = (event as TouchEvent).touches
        ? (event as TouchEvent).touches[0].clientX
        : (event as MouseEvent).clientX;
  
      if (sideMenuLeft < clientX) {
        enteredRef.current = true;
        if (props.id && props.addItem) {
          props.addItem(props.id, "air");
          setShowDialog(false);
        }
      }
    }
  }, []);
  
  useEffect(() => {
    const handleMouseDown = () => {
      document.addEventListener("mousemove", mouseMoveEvent);
      document.addEventListener("mouseup", handleMouseUp);
    };
  
    const handleMouseUp = () => {
      document.removeEventListener("mousemove", mouseMoveEvent);
    };
  
    const handleTouchStart = () => {
      document.addEventListener("touchmove", mouseMoveEvent);
      document.addEventListener("touchend", handleTouchEnd);
    };
  
    const handleTouchEnd = () => {
      document.removeEventListener("touchmove", mouseMoveEvent);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  
    if (popupRef.current) {
      popupRef.current.addEventListener("mouseenter", () => {
        popupRef.current?.addEventListener("mousedown", handleMouseDown);
        popupRef.current?.addEventListener("touchstart", handleTouchStart);
      });
    }
  
    return () => {
      document.removeEventListener("mousemove", mouseMoveEvent);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", mouseMoveEvent);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [mouseMoveEvent]);
  

  return (
    <div
      className="ol-overlay-container ol-selectable"
      style={{
        width: "10px",
        height: "10px",
      }}
    >
      <div
        style={{ visibility: showDialog ? "visible" : "hidden" }}
        ref={parentContainerRef}
      >
        <Draggable
          handle=".drag-handle"
          cancel=".no-drag"
          enableUserSelectHack={false}
        >
          <ResizableBox
            className="custom-resizable-box"
            width={dimensions.resizableBoxwidth}
            height={dimensions.resizableBoxheight}
            minConstraints={[
              dimensionsMin.resizableBoxMinwidth,
              dimensionsMin.resizableBoxMinheight,
            ]}
            onResize={handleResize}
            /* maxConstraints={[800, 600]} */ resizeHandles={[
              "se",
              "sw",
              "ne",
              "nw",
            ]}
            style={{ zIndex: 1, left: "-30vw", right: "0vw", top: "-40vh" }}
          >
            <div
              className="drag-handle"
              ref={fullscreenModal}
              style={{
                bottom: 0,
                margin: "auto",
                width: "100%", //"65vw",
                height: "100%", //"60vh,"
                position: "absolute",
                cursor: "move",
                touchAction: "none",
              }}
            >
              <div className={`airQualityPopup ${isDark ? "dark" : ""}`}>
                <div className="popupBoxContainerAirquality">
                  {/* Sensor popup title container */}
                  <section className="titleContainerAirquality" ref={popupRef}>
                    {/* Sensor popup title */}
                    <span>Air quality</span>
                    {/* Sensor popup title button container */}
                    <div className="titleButtonContainerAirquality">
                      <div
                        className="titleIcon fullscreenIco"
                        onClick={openContentFullscreen}
                        onTouchStart={openContentFullscreen}
                      ></div>
                      <div
                        className="titleIcon closeIco"
                        onClick={() => {
                          hideDialog(props.sensorId);
                        }}
                        onTouchStart={() => {
                          hideDialog(props.sensorId);
                        }}
                      ></div>
                    </div>
                  </section>

                  {/* Sensor popup data containers */}
                  <section className="dataContainerAirquality">
                    {/* Chart */}
                    <div className="chartAirquality cell">
                      {activeFullScreen && (
                        <div
                          className="no-drag"
                          style={{
                            height: "40vh",
                            width: "95%",
                            display: "flex",
                            alignItems: "start",
                            margin: "0px",
                            flexDirection: "column",
                            justifyContent: "top",
                          }}
                        >
                          <AirQualityChart
                            id={props.sensorId}
                            name={setAqiCondition(props.aqi)}
                            aqi={props.aqi}
                            fullScreen={activeFullScreen}
                            showDialog={showDialog}
                            chartWidth={widthMain * 0.92}
                            chartHeight={heightMain * 0.4}
                            location={props.name}
                          />
                        </div>
                      )}
                      {!activeFullScreen && (
                        <div
                          className="no-drag"
                          style={{
                            height: "100%",
                            width: "100%",
                            display: "flex",
                            alignItems: "start",
                            margin: "0px",
                            flexDirection: "column",
                            justifyContent: "top",
                          }}
                        >
                          <AirQualityChart
                            id={props.sensorId}
                            name={setAqiCondition(props.aqi)}
                            aqi={props.aqi}
                            fullScreen={activeFullScreen}
                            showDialog={showDialog}
                            chartWidth={widthMain * 0.92}
                            chartHeight={heightMain * 0.6}
                            location={props.name}
                          />
                        </div>
                      )}
                      {/* Chart here */}
                      {/* Temp start */}
                      {/* {selectedGauges.map(gauge => (
                            <span>{gauge},</span>
                        ))}
                        {timeGap && (
                            <span>{new Date(timeGap.startTime).toLocaleString()} to {new Date(timeGap.endTime).toLocaleString()}</span>
                        )} */}
                      {/* Temp end */}
                    </div>

                    {/* Colors */}
                    <div className="colors cell">
                      <div className="legend">
                        <div style={{ backgroundColor: "#03FE03" }}></div>
                        <span>Good</span>
                      </div>
                      <div className="legend">
                        <div style={{ backgroundColor: "#FFFF33" }}></div>
                        <span>Moderate</span>
                      </div>
                      <div className="legend">
                        <div style={{ backgroundColor: "#D4A017" }}></div>
                        <span>Unhealthy for sensitive group</span>
                      </div>
                      <div className="legend">
                        <div style={{ backgroundColor: "#F1594C" }}></div>
                        <span>Unhealthy</span>
                      </div>
                      <div className="legend">
                        <div style={{ backgroundColor: "#6C2DC7" }}></div>
                        <span>Very unhealthy</span>
                      </div>
                      <div className="legend">
                        <div style={{ backgroundColor: "#7B1515" }}></div>
                        <span>Hazardous</span>
                      </div>
                    </div>
                    {/* Details */}
                    <div className="detailsAirquality1 cell">

                      <div>
                        <span className="label">Date & Time: </span>
                        <span className="data">
                          {date}, {time}
                        </span>
                      </div>
                      <div>
                        <span className="label">AQI :</span>
                        <span className="data">{Math.round(props.aqi)}</span>
                      </div>
                      <div>
                        <span className="label">Last Update : </span>
                        <span className="data">{lastUpdate}</span>
                      </div>
                      <div>
                        <span className="label">Source : </span>
                        <span className="data">NBRO</span>
                      </div>
                    </div>
                    <div className="detailsAirquality2 cell">

                      <div>
                        <span className="label">PM2.5 : </span>
                        <span className="data">{props.pm25} μg/m3</span>
                      </div>
                      <div>
                        <span className="label">PM10 :</span>
                        <span className="data">{props.pm100} μg/m3</span>
                      </div>
                      <div>
                        <span className="label">CO&#8322; : </span>
                        <span className="data">{props.co2} ppm</span>
                      </div>
                      <div>
                        <span className="label">Temperature : </span>
                        <span className="data">{props.temperature} °C</span>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
              {/* <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  padding: "3px 5px",
                  backgroundColor: "rgba(85, 84, 95, 0.4)",
                    height:"6%"
                }}
              >
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                ></div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "rgba(255,255,255, 0.8)",
                  }}
                >
                  Air Quality
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <FontAwesomeIcon
                    onClick={openContentFullscreen}
                    style={{
                      borderRadius: "50%",
                      margin: "5px 10px",
                      width: "20px",
                      height: "20px",
                      color: "#dc143c",
                      cursor: "pointer",
                    }}
                    size="xl"
                    icon={activeFullScreen ? faCompress : faExpand}
                  />
                  <FontAwesomeIcon
                    onClick={hideDialog}
                    onTouchEnd={hideDialog}
                    style={{
                      border: "3px solid #dc143c",
                      borderRadius: "50%",
                      margin: "5px 0px",
                      width: "20px",
                      height: "20px",
                      color: "#dc143c",
                      cursor: "pointer",
                    }}
                    size="xl"
                    icon={faClose}
                  />
                </div>
              </div>
              <div
                style={{
                  overflowY: "auto",
                  height: activeFullScreen ? "90vh" : "90%",
                  width: "100%",
                  msOverflowStyle: "none", // Hide scrollbar in Internet Explorer and Edge
                  scrollbarWidth: "none", // Hide scrollbar in Firefox
                  // Hide scrollbar in Chrome, Safari, and other Webkit-based browsers
                  scrollbarColor: "transparent transparent",
                }}
              >
                <style>
                  {`
                   div::-webkit-scrollbar {
                     display: none;
                   }
                 `}
                </style>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    width: "100%",
                    height: "10%",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: "#FFFFFF",
                    }}
                  >
                    {props.name}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    margin: "0px 0px",
                  }}
                >
                  <div
                    style={{
                      height: "40%",
                      display: "flex",
                      // minWidth: "65vw",
                      margin: "0px",
                      width: "100%",
                    }}
                  >
                    {activeFullScreen && (
                      <div
                        className="no-drag"
                        style={{
                          height: "40vh",
                          width: "95%",
                          display: "flex",
                          alignItems: "start",
                          margin: "0px",
                          flexDirection: "column",
                          justifyContent: "top",
                        }}
                      >
                        <AirQualityChart
                          id={props.sensorId}
                          name={setAqiCondition(props.aqi)}
                          aqi={props.aqi}
                          fullScreen={activeFullScreen}
                          showDialog={showDialog}
                          chartWidth={widthMain}
                          chartHeight={heightMain*.4}
                        />
                      </div>
                    )}
                    {!activeFullScreen && (
                      <div
                        className="no-drag"
                        style={{
                          height: "100%",
                          width: "100%",
                          display: "flex",
                          alignItems: "start",
                          margin: "0px",
                          flexDirection: "column",
                          justifyContent: "top",
                        }}
                      >
                        <AirQualityChart
                          id={props.sensorId}
                          name={setAqiCondition(props.aqi)}
                          aqi={props.aqi}
                          fullScreen={activeFullScreen}
                          showDialog={showDialog}
                          chartWidth={widthMain}
                          chartHeight={heightMain*.6}
                        />
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "auto",
                      margin: "0px 0px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "auto",
                        margin: "10px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          marginRight: "2.5px",
                          width: "10px",
                          height: "10px",
                          border: "1px solid blue",
                          backgroundColor: "#03FE03",
                        }}
                      ></div>
                      <div
                        style={{
                          display: "flex",
                          marginLeft: "2.5px",
                          width: "75px",
                          height: "10px",
                          fontSize: "10px",
                          fontWeight: "bold",
                          fontFamily: "Arial, Helvetica, sans-serif",
                          color: "#008000",
                        }}
                      >
                        Good
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "auto",
                        margin: "10px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          marginRight: "2.5px",
                          width: "10px",
                          height: "10px",
                          border: "1px solid rgb(85, 84, 95)",
                          backgroundColor: "#FFFF33",
                        }}
                      ></div>
                      <div
                        style={{
                          display: "flex",
                          marginLeft: "2.5px",
                          width: "75px",
                          height: "10px",
                          fontSize: "10px",
                          fontWeight: "bold",
                          fontFamily: "Arial, Helvetica, sans-serif",
                          color: "#F5E216",
                        }}
                      >
                        Moderate
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "auto",
                        margin: "10px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          marginRight: "2.5px",
                          width: "10px",
                          height: "10px",
                          border: "1px solid rgb(85, 84, 95)",
                          backgroundColor: "#D4A017",
                        }}
                      ></div>
                      <div
                        style={{
                          display: "flex",
                          marginLeft: "2.5px",
                          width: "75px",
                          height: "10px",
                          fontSize: "10px",
                          fontWeight: "bold",
                          fontFamily: "Arial, Helvetica, sans-serif",
                          color: "#CD7F32",
                        }}
                      >
                        Unhealthy for sensitive group
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "auto",
                        margin: "10px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          marginRight: "2.5px",
                          width: "10px",
                          height: "10px",
                          border: "1px solid rgb(85, 84, 95)",
                          backgroundColor: "#F1594C",
                        }}
                      ></div>
                      <div
                        style={{
                          display: "flex",
                          marginLeft: "2.5px",
                          width: "75px",
                          height: "10px",
                          fontSize: "10px",
                          fontWeight: "bold",
                          fontFamily: "Arial, Helvetica, sans-serif",
                          color: "#F93D2B",
                        }}
                      >
                        Unhealthy
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "auto",
                        margin: "10px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          marginRight: "2.5px",
                          width: "10px",
                          height: "10px",
                          border: "1px solid rgb(85, 84, 95)",
                          backgroundColor: "#6C2DC7",
                        }}
                      ></div>
                      <div
                        style={{
                          display: "flex",
                          marginLeft: "2.5px",
                          width: "75px",
                          height: "10px",
                          fontSize: "10px",
                          fontWeight: "bold",
                          fontFamily: "Arial, Helvetica, sans-serif",
                          color: "#461B7E",
                        }}
                      >
                        Very unhealthy
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "auto",
                        margin: "10px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          marginRight: "2.5px",
                          width: "10px",
                          height: "10px",
                          border: "1px solid rgb(85, 84, 95)",
                          backgroundColor: "#7B1515",
                        }}
                      ></div>
                      <div
                        style={{
                          display: "flex",
                          marginLeft: "2.5px",
                          width: "75px",
                          height: "10px",
                          fontSize: "10px",
                          fontWeight: "bold",
                          fontFamily: "Arial, Helvetica, sans-serif",
                          color: "#7B1515",
                        }}
                      >
                        Hazardous
                      </div>
                    </div>
                  </div>
                  <div>
                    {showDescription ? (
                      <AirQualityDescription
                        pm25={props.pm25}
                        pm100={props.pm100}
                        co2={props.co2}
                        temperature={props.temperature}
                        aqi={props.aqi}
                        timestamp={props.timestamp}
                      />
                    ) : (
                      <div
                        style={{
                          height: "60px", // Increased height to accommodate two rows
                          borderRadius: "3px",
                          display: "flex",
                          flexWrap: "wrap", // Enable wrapping
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "0px solid",
                          backgroundColor: "rgba(35,38,47,255)",
                          margin: "20px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            marginLeft: "3%",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "start",
                              marginRight: "10px", // Adjust margin for even spacing
                              width: "25%", // Make sure elements take up 1/4th of the width
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: "white",
                              }}
                            >
                              Date :&#160;&#160;
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: "white",
                              }}
                            >
                              {date}, {time}
                            </div>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "start",
                              marginRight: "10px",
                              width: "25%",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: "white",
                              }}
                            >
                              AQI :&#160;&#160;
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: "white",
                              }}
                            >
                              {Math.round(props.aqi)}
                            </div>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "start",
                              marginRight: "10px",
                              width: "25%",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: "white",
                              }}
                            >
                              Last Update :&#160;&#160;
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: "white",
                              }}
                            >
                              {lastUpdate}
                            </div>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "start",
                              marginRight: "10px",
                              width: "25%",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: "white",
                              }}
                            >
                              Source :&#160;&#160;
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: "white",
                              }}
                            >
                              NBRO
                            </div>
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            marginLeft: "3%",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "start",
                              marginRight: "10px",
                              width: "25%", // Elements now take 1/3rd of the width
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: "white",
                              }}
                            >
                              PM2.5 :&#160;&#160;
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: "white",
                              }}
                            >
                              {props.pm25} μg/m3
                            </div>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "start",
                              marginRight: "10px",
                              width: "25%",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: "white",
                              }}
                            >
                              PM10 :&#160;&#160;
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: "white",
                              }}
                            >
                              {props.pm100} μg/m3
                            </div>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "start",
                              marginRight: "10px",
                              width: "25%",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: "white",
                              }}
                            >
                              CO&#8322; :&#160;&#160;
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: "white",
                              }}
                            >
                              {props.co2} ppm
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "start",
                              marginRight: "10px",
                              width: "25%",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: "white",
                              }}
                            >
                              Temperature :&#160;&#160;
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: "white",
                              }}
                            >
                              {props.temperature} °C
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="resize-dot"></div> */}
            </div>
          </ResizableBox>
        </Draggable>
      </div>
    </div>
  );
};

export default AirQualityPopup;
